import { GridSize, useMediaQuery, useTheme } from '@mui/material';
import { Breakpoint } from '@mui/material/styles';

export type BreakpointSizes = {
  xs: GridSize;
  sm: GridSize;
  md: GridSize;
  lg: GridSize;
  xl: GridSize;
};

export type ActiveBreakpoint = {
  xs: boolean;
  sm: boolean;
  md: boolean;
  lg: boolean;
  xl: boolean;
};

const getActiveBreakpoint = (activeBreakpoint: ActiveBreakpoint): Breakpoint => {
  if (activeBreakpoint.sm) {
    return 'sm';
  }

  if (activeBreakpoint.md) {
    return 'md';
  }

  if (activeBreakpoint.lg) {
    return 'lg';
  }

  if (activeBreakpoint.xl) {
    return 'xl';
  }

  return 'xs';
};

export const MIN_WIDTH_CUSTOMIZED_TABLET = 768;
export const MAX_WIDTH_CUSTOMIZED_TABLET = 1279.5;

const useWidth = () => {
  const theme = useTheme();
  const mobileWidth = useMediaQuery(theme.breakpoints.down('sm'), { noSsr: true });
  const smallWidth = useMediaQuery(theme.breakpoints.down('md'), { noSsr: true });
  const mediumDownWidth = useMediaQuery(theme.breakpoints.down('lg'), { noSsr: true });
  const isDownXl = useMediaQuery(theme.breakpoints.down('xl'), { noSsr: true });
  const horizontalTabletWidth = useMediaQuery(
    `(min-width:1024px) and (max-width:${MAX_WIDTH_CUSTOMIZED_TABLET}px)`,
    {
      noSsr: true,
    },
  );
  const desktopWideWidth = useMediaQuery('(min-width:1366px) and (max-width:1919px)', {
    noSsr: true,
  });
  const isCustomizedTablet = useMediaQuery(
    `(min-width:${MIN_WIDTH_CUSTOMIZED_TABLET}px) and (max-width:${MAX_WIDTH_CUSTOMIZED_TABLET}px)`,
    {
      noSsr: true,
    },
  );
  const isCustomizedTabletUp = useMediaQuery(`(min-width:${MIN_WIDTH_CUSTOMIZED_TABLET}px)`, {
    noSsr: true,
  });
  const isCustomizedTabletDown = useMediaQuery(`(max-width:${MAX_WIDTH_CUSTOMIZED_TABLET}px)`, {
    noSsr: true,
  });
  const largeWidthUp = useMediaQuery(theme.breakpoints.up('lg'), { noSsr: true });
  const largeWidthDown = useMediaQuery(theme.breakpoints.down('xl'), { noSsr: true });
  const extraLargeWidth = useMediaQuery(theme.breakpoints.only('xl'), { noSsr: true });

  const activeBreakpoint = {
    xs: useMediaQuery(theme.breakpoints.only('xs'), { noSsr: true }),
    sm: useMediaQuery(theme.breakpoints.only('sm'), { noSsr: true }),
    md: useMediaQuery(theme.breakpoints.only('md'), { noSsr: true }),
    lg: useMediaQuery(theme.breakpoints.only('lg'), { noSsr: true }),
    xl: useMediaQuery(theme.breakpoints.only('xl'), { noSsr: true }),
  };

  return {
    isDownXl,
    desktopWideWidth,
    isCustomizedTablet,
    mobileWidth,
    smallWidth,
    mediumDownWidth,
    horizontalTabletWidth,
    extraLargeWidth,
    largeWidthUp,
    largeWidthDown,
    isCustomizedTabletUp,
    isCustomizedTabletDown,
    activeBreakpoint: getActiveBreakpoint(activeBreakpoint),
  };
};

export default useWidth;
